import type { EnergyEfficiencyClass } from '@einwert/valuation-api-client'
import { Box, Stack } from '@mui/material'
import { ResponsivePie } from '@nivo/pie'
import { decimalToLocalizedString, EmptyState, tokens } from 'ComponentLibrary'
import { EnergyEfficiencyTooltip } from './EnergyEfficiencyChartTooltip'
import { ENERGY_CLASS_LABELS, energyClassColors } from './constants'

import { sumBy } from 'lodash'
import { Headline } from 'ComponentLibrary/src/piazza/text/Headline/Headline'
import { nivoTooltipWrapperTheme } from 'ComponentLibrary/src/common/nivoTheme'
import { useTranslation } from 'react-i18next'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'

export type EnergyEfficiencyClassItem = {
    energy_efficiency_class: EnergyEfficiencyClass
    count: number
}

export type EnergyEfficiencyChartProps = {
    data: EnergyEfficiencyClassItem[]
    title: string
}

const EnergyEfficiencyChart = ({ data, title }: EnergyEfficiencyChartProps) => {
    const { t: tCommon } = useTranslation('terminology', { keyPrefix: 'common' })
    const locale = useCurrentResolvedLocale()
    const coloredData = data.map((efficiencyClass) => ({
        ...efficiencyClass,
        id: ENERGY_CLASS_LABELS[efficiencyClass.energy_efficiency_class],
        label: ENERGY_CLASS_LABELS[efficiencyClass.energy_efficiency_class],
        value: efficiencyClass.count,
        color: energyClassColors[efficiencyClass.energy_efficiency_class],
    }))

    const total = sumBy(coloredData, 'count')
    return (
        <Stack height="90%" width="100%" direction="column" gap={'16px'}>
            <Headline variant="headline2" title={title} />
            {total === 0 ? (
                <EmptyState title={tCommon('notEnoughDataAvailableYet')} size="medium" textColor={tokens.ColorTextDefault} />
            ) : (
                <Stack direction="row" gap={'16px'} alignItems="center">
                    <Box height="162px" width="162px" marginX="7px" flexGrow={1}>
                        <ResponsivePie
                            data={coloredData}
                            arcLabel="label"
                            arcLabelsTextColor="#ffffff"
                            arcLabelsSkipAngle={15}
                            margin={{ top: 4, right: 8, bottom: 4, left: 8 }}
                            innerRadius={0.75}
                            padAngle={2}
                            cornerRadius={2}
                            activeOuterRadiusOffset={2}
                            borderWidth={0}
                            colors={{ datum: 'data.color' }}
                            layers={['arcs', 'arcLabels']}
                            theme={nivoTooltipWrapperTheme}
                            valueFormat={(value) => `${decimalToLocalizedString(value, false, 2, undefined, locale)}`}
                            tooltip={({ datum }) => <EnergyEfficiencyTooltip datum={datum} total={total} />}
                        />
                    </Box>
                </Stack>
            )}
        </Stack>
    )
}

export default EnergyEfficiencyChart
